<template>
  <div v-resize="calcSVGLine">
    <v-container ref="targetMatrCont" class="pa-0" style="position: relative;">
      <v-sheet v-html="svgLine"/>
      <fin-enum-selector :key="index" :tooltips="val.values.map((item) => { return item.tooltip })"
                         ref="enumSelector" v-for="(val, index) in archetypeClassifications" :individual="individual"
                         v-model="workingData[val.name]" :col-number="index" :readonly="readonly"
                         class="archetype-property"
                         :style="$vuetify.breakpoint.xs ? 'font-size: 13px' : 'font-size: 14px'">
        <template #prepend>
          {{ val.values[0].title }}
        </template>
        <template #append>
          {{ val.values[2].title }}
        </template>
      </fin-enum-selector>
    </v-container>
  </div>
</template>

<script>
import FinEnumSelector from '@/components/common/enumSelector/index'

/**
 * A component to show and edit a target matrix
 */
export default {
  name: 'finTargetMatrix',
  components: {FinEnumSelector},
  props: {
    /**
     * @model
     * the target matrix value
     */
    value: {
      type: Object
    },
    /** Puts the input in readonly state */
    readonly: {
      type: Boolean,
      default: false
    },
    /** a list of archetype classifications */
    individual: {},
    /** a list of archetype classifications */
    archetypeClassifications: {
      type: Array,
      default: () => []
    },
    /** if the svg should be drawn initially */
    initialDraw: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      workingData: this.value,
      svgLine: null,
      check: false
    }
  },
  methods: {
    calcSVGLine() {
      if (!this.check && !this.individual) {
        return
      }
      if (!this.$refs.enumSelector) {
        return
      }

      const entries = Object.entries(this.workingData)

      if (entries.length <= 1) {
        return
      }

      const iconH = 24
      const responsiveColPadding = 12

      let contW = this.$refs.targetMatrCont.getBoundingClientRect().width - 2 * responsiveColPadding
      const contH = this.$refs.targetMatrCont.getBoundingClientRect().height

      // TODO find a better solution, the following is just a workaround!
      // fix xs Bug where contW seems to be -24
      if (contW <= 0) {
        contW = 300
      }

      let result = '<svg viewBox="0 0 ' + contW + ' ' + contH + '" version="1.1" xmlns="http://www.w3.org/2000/svg" class="svg-box">'

      entries.forEach(([key, value], index) => {
        if (index < entries.length - 1) {
          let cur = index
          let nextEntry = entries[index + 1]

          // don't try to draw a line from undefined selections to the next one -> skip undefined element
          if (this.$refs.enumSelector[cur].getSelectedXPosition() === 0) {
            return
          }

          // avoid console error if next entry is null/not selected
          if (nextEntry[1] === null) {
            return
          }

          result += '<path class="svg-line" d="'

          const startX = this.$refs.enumSelector[cur].getSelectedXPosition() - responsiveColPadding
          const startY = this.$refs.enumSelector[cur].$el.getBoundingClientRect().top + responsiveColPadding + iconH - this.$refs.targetMatrCont.getBoundingClientRect().top

          result += 'M ' + startX + ' ' + startY

          const firstB = this.$refs.enumSelector[cur].getSelectedBottomPosition()

          cur++
          const endX = this.$refs.enumSelector[cur].getSelectedXPosition() - responsiveColPadding

          // if next element isn't selected too -> skip drawing line
          if (endX <= 0) {
            return
          }

          const secondT = this.$refs.enumSelector[cur].getSelectedTopPosition()

          const distance = secondT - firstB + responsiveColPadding /* */
          const splinePointH = distance * 0.5

          // connect left to right
          if (startX < endX) {
            result += ' C ' + startX + ' ' + (startY + splinePointH) + ', ' +
              endX + ' ' + (startY + splinePointH) + ', ' +
              endX + ' ' + (startY + distance)
          }
          // connect right to left
          else if (startX > endX) {
            result += ' C ' + startX + ' ' + (startY + splinePointH) + ', ' +
              endX + ' ' + (startY + splinePointH) + ', ' +
              endX + ' ' + (startY + distance)
          }
          // directly connect via line
          else {
            result += ' V ' + (startY + distance)
          }
        }

        result += '" />'
      })

      result += '</svg>'

      this.svgLine = result
    }
  },
  mounted() {
    if (this.initialDraw) {
      this.$nextTick(() => {
        // calcSVGLine method is called before mounted, so we need variable check to avoid console error
        this.check = true
        this.calcSVGLine()
      })
    }
  },
  watch: {
    value: {
      handler() {
        this.workingData = this.value
      },
      deep: true
    },
    workingData: {
      handler() {
        /**
         * Input change event.
         *
         * @event input
         * @type {object}
         */
        this.$emit('input', this.workingData)

        this.$nextTick(this.calcSVGLine)
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .svg-box {
  width: 100%;
  height: 100%;
  fill: blue;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
}

::v-deep .svg-line {
  stroke-width: 2px;
  stroke: rgba(97, 195, 120, 1);
  fill: transparent;
}

.archetype-property {
  height: 40px;
}
</style>
